"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPES = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    zip: "",
    projectType: "Basement",
    timing: "",
    budget: "",
    message: "",
    consent: false
  });
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === "zip" && zipError) {
      if (/^\d{5}$/.test(value.trim())) {
        setZipError("");
      }
    }
  };
  const handleConsentChange = (checked: boolean | "indeterminate") => {
    const val = checked === true;
    setFormData(prev => ({
      ...prev,
      consent: val
    }));
    if (val && consentError) {
      setConsentError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    let valid = true;
    if (!/^\d{5}$/.test(formData.zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      valid = false;
    } else {
      setZipError("");
    }
    if (!formData.consent) {
      setConsentError("Please confirm your consent to be contacted.");
      valid = false;
    } else {
      setConsentError("");
    }
    if (!valid) return;
    setIsSubmitting(true);
    try {
      const currentPageUrl = typeof window !== "undefined" ? window.location.href : "";
      const bodyParams = new URLSearchParams({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        project_type: formData.projectType,
        address: formData.zip.trim(),
        zip: formData.zip.trim(),
        details: formData.message.trim(),
        timing: formData.timing,
        budget: formData.budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: currentPageUrl,
        form_location: "basement-remodeling-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: bodyParams
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-foreground">
            Request your basement estimate
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Tell us about your finished basement goals and schedule an on-site consultation in Brookhaven.
          </p>
        </div>

        <div className="rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 md:p-12 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]">
          {isSuccess ? <div className="text-center py-10 space-y-4">
              <div className="inline-flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary mb-2">
                <CheckCircle2 className="size-8" />
              </div>
              <h3 className="text-2xl font-serif font-medium text-foreground">
                Estimate request received
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Thank you for reaching out. A Brookhaven Elevation Renovations specialist will review your basement project and get in touch shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {errorMessage && <Alert variant="destructive" className="border-destructive/30">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to submit form</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="field-name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={handleInputChange} className="h-11 bg-background" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-phone" name="phone" type="tel" required placeholder="(404) 555-0123" value={formData.phone} onChange={handleInputChange} className="h-11 bg-background" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-email" name="email" type="email" required placeholder="jane@example.com" value={formData.email} onChange={handleInputChange} className="h-11 bg-background" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-zip">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="field-zip" name="zip" type="text" required maxLength={5} placeholder="30319" value={formData.zip} onChange={handleInputChange} aria-invalid={!!zipError} className={`h-11 bg-background ${zipError ? "border-destructive ring-destructive/20" : ""}`} />
                  {zipError && <p className="text-xs text-destructive mt-1 font-medium">
                      {zipError}
                    </p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-project-type">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="field-project-type" name="projectType" required disabled value={formData.projectType} onChange={handleInputChange} className="flex h-11 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-foreground shadow-xs cursor-not-allowed opacity-90 focus-visible:outline-none">
                    {PROJECT_TYPES.map((type, i) => <option key={i} value={type} data-index={i}>
                        {type}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-timing">Preferred Timing</Label>
                  <select id="field-timing" name="timing" value={formData.timing} onChange={handleInputChange} className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none">
                    <option value="">Select timing preference</option>
                    {TIMING_OPTIONS.map((time, i) => <option key={i} value={time} data-index={i}>
                        {time}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="field-budget">Budget Range</Label>
                  <select id="field-budget" name="budget" value={formData.budget} onChange={handleInputChange} className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none">
                    <option value="">Select estimated budget</option>
                    {BUDGET_OPTIONS.map((budget, i) => <option key={i} value={budget} data-index={i}>
                        {budget}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="field-message">Project Details / Message</Label>
                <Textarea id="field-message" name="message" rows={4} placeholder="Share details about your basement project, current layout, desired features, or key requirements..." value={formData.message} onChange={handleInputChange} className="bg-background min-h-28" />
              </div>

              <div className="space-y-1.5 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="field-consent" checked={formData.consent} onCheckedChange={handleConsentChange} className="mt-0.5" />
                  <Label htmlFor="field-consent" className="text-xs sm:text-sm text-muted-foreground font-normal leading-snug cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive font-medium pl-7">
                    {consentError}
                  </p>}
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto min-w-48 text-base font-medium h-12">
                  {isSubmitting ? "Sending request..." : <>
                      <span>Submit estimate request</span>
                      <Send className="size-4 ml-1" />
                    </>}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}